import * as moment from 'moment';
import { IRecipient } from '@nkr/models/recipient';
import { Button, FormControl, Label, OverlayTrigger } from 'react-bootstrap';
import { DeclineRatio } from './decline-ratio-type';
import { styles } from './styles';
import { questionCircleO } from 'react-icons-kit/fa/questionCircleO';
import PopoverTemplate from '@nkr/components/popover-template';
import Icon from 'react-icons-kit';

interface IProps {
    recipient: IRecipient,
    declineRatioAllTime: number,
    startDate: string,
    endDate: string,
    declineRatioType: DeclineRatio,
    declineRatioAdjusted: number,
    onSwitchDeclineRatioGrid: (declineRatioType: DeclineRatio) => void,
    onStartDateChange: (date: string) => void,
    onEndDateChange: (date: string) => void,
    onExportCSV: () => void,
    onShowMortalityBreakdown?: (recipient: IRecipient) => void,
    onShowFWTBreakdown?: (recipient: IRecipient) => void
}

const Filter = (props: IProps) => {
    const handleDeclineRatioSwitch = (type: DeclineRatio) => {
        props.onSwitchDeclineRatioGrid(type);
    };

    const handleShowMortalityRiskCalculator = (recipient: IRecipient) => {
        props.onShowMortalityBreakdown(recipient);
    };

    const handleShowForeCastedWaitTimeCalc = (recipient: IRecipient) => {
        props.onShowFWTBreakdown(recipient);
    };

    const handleStartDateChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let date = e.currentTarget.value;
        props.onStartDateChange(date);
    };

    const handleEndDateChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let date = e.currentTarget.value;
        props.onEndDateChange(date);
    };

    if (props.recipient == null)
        return null;

    const drText = parseInt(props.recipient.forecastedWaitTime) < 2 ? ' month' : ' months';

    return (
        <>
            <div style={{ display: 'flex', marginBottom: 10 }}>
                <Button
                    bsStyle={props.declineRatioType == DeclineRatio.Current ? 'primary' : 'default'}
                    style={{ marginRight: 10 }}
                    onClick={() => handleDeclineRatioSwitch(DeclineRatio.Current)}>
                    <strong>{`Current Donor Decline Ratio (${(props.recipient.declineRatioCurrent * 100).toFixed(0)}%)`}</strong>
                </Button>
                <Button
                    bsStyle={props.declineRatioType == DeclineRatio.Historic ? 'primary' : 'default'}
                    style={styles.ratioButton}
                    onClick={() => handleDeclineRatioSwitch(DeclineRatio.Historic)}>
                    <strong>{`Historic Donor Decline Ratio (${(props.recipient.declineRatioHistoric * 100).toFixed(0)}%)`}</strong>
                </Button>
                <Button
                    bsStyle={props.declineRatioType == DeclineRatio.AllTime ? 'primary' : 'default'}
                    onClick={() => handleDeclineRatioSwitch(DeclineRatio.AllTime)}>
                    <strong>{`All Time Donor Decline Ratio (${(props.declineRatioAllTime * 100).toFixed(0)}%)`}</strong>
                </Button>
            </div>
            <div style={{ display: 'flex' }}>
                <div style={{ display: 'flex', marginBottom: 10 }}>
                    <div style={{ marginRight: 10, fontSize: 14 }}>
                        <strong>Patient Alias:</strong>&nbsp;{props.recipient.alias}
                    </div>
                    <div style={{ marginRight: 10, fontSize: 14 }}>
                        <strong>ABO:</strong>&nbsp;{props.recipient.bloodType}
                    </div >
                    <div style={{ marginRight: 10, fontSize: 14 }}>
                        <strong>Age:</strong>&nbsp;{props.recipient.age}
                    </div>
                    <div style={{ marginRight: 10, fontSize: 14 }}>
                        <strong>cPRA:</strong>&nbsp;{props.recipient.cpra4 != null ? (props.recipient.cpra4).toFixed(2) : 0}%
                    </div>
                </div>
                <div style={{ display: 'flex' }}>
                    <div style={{ marginLeft: 5, fontSize: 14, display: 'flex', alignItems: 'center' }}>
                        <div style={{ fontWeight: 700, marginRight: 5 }}>
                            Donor Decline Ratio:
                        </div>
                        <div>
                            {(props.recipient.highestDeclineRatio * 100).toFixed(0)}%
                        </div>
                        <div>
                            <sup>
                                <OverlayTrigger
                                    rootClose
                                    placement='bottom'
                                    trigger={['hover', 'focus']}
                                    overlay={<PopoverTemplate dismiss={false}>The Donor Decline Ratio reflects the higher value between a patient's current decline ratio and their historic decline ratio in preselects from the past three months.</PopoverTemplate>}
                                >
                                    <Icon icon={questionCircleO} size={16} style={{ color: 'blue', marginLeft: 3 }} />
                                </OverlayTrigger>
                            </sup>
                        </div>
                    </div>

                    <div style={{ marginLeft: 15, fontSize: 14, display: 'flex', alignItems: 'center' }}>
                        <div style={{ fontWeight: 700, marginRight: 5 }}>
                            Mortality Risk Calc:
                        </div>
                        <div>
                            {props.recipient.mortalityRiskPercent.toFixed(0) == "0" ? (
                                <span>0%</span>
                            ) : (
                                <Button
                                    bsStyle='link'
                                    style={{ padding: 2 }}
                                    onClick={() => handleShowMortalityRiskCalculator(props.recipient)}
                                >
                                    {props.recipient.mortalityRiskPercent.toFixed(0)}%
                                </Button>
                            )}
                        </div>
                    </div>

                    <div style={{ marginLeft: 10, fontSize: 14, display: 'flex', alignItems: 'center' }}>
                        <div style={{ fontWeight: 700, marginRight: 5 }}>
                            Forecasted Wait Time:
                        </div>
                        <div>
                            {props.recipient.forecastedWaitTime?.includes("N/A") ? (
                                <span>{props.recipient.forecastedWaitTime}</span>
                            ) : (
                                <Button
                                    bsStyle='link'
                                    style={{ padding: 2 }}
                                    onClick={() => handleShowForeCastedWaitTimeCalc(props.recipient)}
                                >
                                    {props.recipient.forecastedWaitTime} {drText}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                <div>
                    {props.declineRatioType == DeclineRatio.Current ?
                        <Label style={{ fontSize: '100%' }}>* This report only includes potential donors that are currently exportable and not in a match</Label>
                        : props.declineRatioType == DeclineRatio.Historic ?
                        <Label style={{ fontSize: '100%' }}>* This report includes potential donors reviewed in preselects from the past 3 months, including those that have already donated</Label>
                        :
                        <Label style={styles.bannerLabel}>* This report includes potential donors reviewed in preselects since the patient's first exportable date, including those that have already donated</Label>
                    }
                </div>

                {props.declineRatioType == DeclineRatio.Current ?
                    <div style={{ display: 'flex' }}>
                        <div>
                            <Button bsSize='sm' onClick={props.onExportCSV}>
                                Export CSV
                            </Button>
                        </div>
                    </div>
                    :
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                            <div style={{ marginRight: 5 }}><strong>Start Date:&nbsp;</strong></div>
                            <div>
                                {props.declineRatioType == DeclineRatio.AllTime
                                    ? (props.recipient.firstExportableDate ? moment(props.recipient.firstExportableDate).format('MM/DD/YYYY') : 'N/A')
                                    : moment().subtract(3, 'months').format('MM/DD/YYYY')}
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                            <div style={{ marginRight: 5 }}><strong>End Date:</strong></div>
                            <div>
                                {moment().format('MM/DD/YYYY')}
                            </div>
                        </div>
                        <div>
                            <Button bsSize='sm' onClick={props.onExportCSV}>
                                Export CSV
                            </Button>
                        </div>
                    </div>
                }
            </div>
        </>
    );
}

export default Filter;